import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../../supabaseClient";
import { getPostLoginRoute, type AppUser } from "../../../auth/AuthProvider";

const getSafeRedirectPath = (pathname?: string | null) => {
  if (!pathname) return null;
  if (!pathname.startsWith("/")) return null;
  if (pathname.startsWith("//")) return null;
  if (pathname === "/login") return null;
  if (pathname === "/signup") return null;
  if (pathname === "/auth/callback") return null;
  return pathname;
};

const mapSessionUserToAppShape = (sessionUser: any): AppUser => {
  const metadata = sessionUser?.user_metadata ?? {};
  const appMetadata = sessionUser?.app_metadata ?? {};

  const rawRole =
    metadata.role ??
    appMetadata.role ??
    metadata.userRole ??
    appMetadata.userRole ??
    null;

  const normalizedRole =
    typeof rawRole === "string"
      ? rawRole.trim().toLowerCase() === "spowner"
        ? "provider_owner"
        : rawRole.trim().toLowerCase() === "hnbstaff"
          ? "hnb_staff"
          : rawRole.trim().toLowerCase()
      : null;

  return {
    id: sessionUser?.id ?? "",
    name: metadata.full_name || sessionUser?.email || "User",
    email: sessionUser?.email || "",
    role:
      normalizedRole === "customer" ||
      normalizedRole === "provider_owner" ||
      normalizedRole === "stylist" ||
      normalizedRole === "hnb_staff"
        ? normalizedRole
        : null,
    providerStatus:
      typeof metadata.providerStatus === "string"
        ? metadata.providerStatus
        : typeof appMetadata.providerStatus === "string"
          ? appMetadata.providerStatus
          : undefined,
    providerOnboardingCompleted:
      typeof metadata.providerOnboardingCompleted === "boolean"
        ? metadata.providerOnboardingCompleted
        : typeof appMetadata.providerOnboardingCompleted === "boolean"
          ? appMetadata.providerOnboardingCompleted
          : false,
    providerOnboardingStep:
      typeof metadata.providerOnboardingStep === "string"
        ? metadata.providerOnboardingStep
        : typeof appMetadata.providerOnboardingStep === "string"
          ? appMetadata.providerOnboardingStep
          : undefined,
  };
};

const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let isActive = true;

    const finishAuth = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const requestedRedirect = getSafeRedirectPath(searchParams.get("redirect"));

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!isActive) return;

        if (session?.user) {
          const nextUser = mapSessionUserToAppShape(session.user);
          navigate(requestedRedirect || getPostLoginRoute(nextUser), {
            replace: true,
          });
        } else {
          navigate("/login", { replace: true });
        }
      } catch (error) {
        console.error("Auth callback error", error);

        if (isActive) {
          navigate("/login", { replace: true });
        }
      }
    };

    finishAuth();

    return () => {
      isActive = false;
    };
  }, [location.search, navigate]);

  return (
    <main
      style={{
        minHeight: "calc(100vh - 160px)",
        display: "grid",
        placeItems: "center",
        padding: "3rem 1.25rem",
        background: "#f8fafc",
        color: "#334155",
      }}
    >
      Completing sign-in...
    </main>
  );
};

export default AuthCallbackPage;